import { useEffect } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

import PageContainer from "@components/PageContainer";
import { SlidesStepperNavButtons, SlideStepper, SlideStepperBreadcrumbs } from "@components/Slides/SlideStepper";
import TabGroup from "@components/Tabs/TabGroup";

import routes from "@constants/routes";

import CompaniesContacts from "@pages/Companies/components/CompaniesContacts";
import TechnologyCampaigns from "@pages/Technology/components/TechnologyCampaigns";
import TechnologyCompanies from "@pages/Technology/components/TechnologyCompanies";
import TechnologyContacts from "@pages/Technology/components/TechnologyContacts";
import TechnologyDetails from "@pages/Technology/components/TechnologyDetails";
import TechnologyMarketingSummary from "@pages/Technology/components/TechnologyMarketingSummary";
import useTechnologyPageContent from "@pages/Technology/hooks/useTechnologyPageContent";

import { useCampaignsQuery } from "@queries/campaign.query";
import { useTechnologyQuery } from "@queries/portfolio.query";

import { useTableStore } from "@store/filters/filters.store";
import { useOrganizationStore } from "@store/organizationStore";
import { useUserStore } from "@store/userStore";

export default function Technology() {
  const user = useUserStore();

  const navigate = useNavigate();
  const { technologyId } = useParams();

  const { organization } = useOrganizationStore();

  const { data: technology, isSuccess: technologyLoadSuccess } = useTechnologyQuery(technologyId);
  const { breadcrumbs, currentView, technologyNavigationTabs, technologyViews, viewIndex } = useTechnologyPageContent();
  const { status: campaignsLoadStatus } = useCampaignsQuery(technologyId);

  const { resetTableStore: resetTechnologyCompany } = useTableStore("technologyCompany");
  const { resetTableStore: resetTechnologyContact } = useTableStore("technologyContact");

  /**
   * Reset table store when unmounting the component so that
   * the table states are not persisted between technology pages
   */
  useEffect(() => {
    return () => {
      resetTechnologyCompany();
      resetTechnologyContact();
    };
  }, []);

  if (!technologyLoadSuccess || campaignsLoadStatus === "pending") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
        }}
      >
        <LinearProgress />
      </Box>
    );
  }

  // ensure technologyId exists
  if (!technologyId) {
    if (organization) return <Navigate to={`${routes.ORGANIZATION}`} />;
    return <Navigate to={`${routes.PORTFOLIO}`} />;
  }

  // Redirect partially correct technology urls to the overview tab
  // TODO: Uncomment once /overview is working
  // if (!currentView) return <Navigate to={`${routes.TECHNOLOGY}/${technologyId}/overview`} />;
  if (!currentView) return <Navigate to={`${routes.TECHNOLOGY}/${technologyId}/details`} />;

  return (
    <PageContainer>
      <SlideStepper>
        <SlideStepperBreadcrumbs
          breadcrumbs={breadcrumbs}
          handleClickIndex={(index) => breadcrumbs[index]?.onClick()}
          index={1}
          workflow="smart-search"
        />
        <SlidesStepperNavButtons
          backButtonText={currentView.backButtonText}
          backButtonTooltip={currentView.backButtonTooltip}
          handleBack={currentView.handleBack}
          handleNext={currentView.handleNext}
          isBackButtonDisabled={currentView.isBackButtonDisabled}
          isBackButtonPending={false}
          isNextButtonDisabled={currentView.isNextButtonDisabled}
          isNextButtonPending={currentView.isBackButtonDisabled}
          nextButtonStyle={currentView.nextButtonStyle}
          nextButtonText={currentView.nextButtonText}
          nextButtonTooltip={currentView.nextButtonTooltip}
          showNextButton={currentView.showNextButton}
        />
      </SlideStepper>
      {technologyLoadSuccess && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          {currentView.isTab && (
            <TabGroup
              index={viewIndex}
              setIndex={(index) => {
                if (technologyViews[index]) {
                  navigate(`${routes.TECHNOLOGY}/${technologyId}/${technologyViews[index].splatString}`);
                }
              }}
              sx={{ minHeight: "50px" }}
              tabs={technologyNavigationTabs}
            />
          )}
          <Routes>
            {/* TODO: Connect this to actual data: <Route element={<TechnologyOverview />} path="/overview" />*/}
            <Route
              element={
                <TechnologyDetails
                  technologyDescription={technology.description ?? undefined}
                  technologyId={technologyId}
                  technologyName={technology.name}
                />
              }
              path="/details"
            />
            <Route element={<TechnologyCompanies />} path="/companies" />
            <Route
              element={
                <TechnologyContacts
                  contacts={technology.contacts}
                  shouldUseTabHeight
                  technologyId={technologyId}
                  technologyName={technology.name}
                />
              }
              path="/contacts"
            />
            <Route element={<TechnologyMarketingSummary technologyId={technologyId} />} path="/marketing-summary" />
            <Route element={<TechnologyCampaigns />} path="/campaigns" />
            <Route element={<CompaniesContacts />} path="/companies/contact-results" />
          </Routes>
        </Box>
      )}
    </PageContainer>
  );
}
